import React from 'react';
import moment from 'moment';
import qs from 'qs';
import { getFormattedCurrencyWithIdealDenomination } from 'utils/number';
import { groupBy } from 'lodash';
import { VENDOR_TYPES } from 'Details/utils';
import { getQueryParams } from 'utils/query-string';
import { getCompaniesWithDefaults } from 'Pmi/HeroChart/utils';
import { HighlightedText } from './SearchDialog.styles';

const SPEND_TYPE = {
	INDIRECT: '1',
	DIRECT: '0',
};

export const COMPANY_FILTER_KEY = 'company';
export const dateFormatter = dateStr => moment(dateStr).format('MMM Do, YYYY');

export const colFormatters = {
	maxDate: dateFormatter,
	minDate: dateFormatter,
	binnedDate: dateFormatter,
	spend: (value, currencySymbol) =>
		`${currencySymbol}${getFormattedCurrencyWithIdealDenomination({
			value,
			isUnitSign: true,
			hideDecimals: false,
		})}`,
};

export const SELECTED_DIMENSIONS_LOCAL_STORAGE_KEY =
	'search-selected-dimensions';

export const getHighlightedName = (name, searchText) => {
	if (!searchText) {
		return name;
	}
	const index = name.toLowerCase().indexOf(searchText.toLowerCase());
	const before = name.slice(0, index);
	const searchValue = name.slice(index, index + searchText.length);
	const after = name.slice(index + searchText.length, name.length);

	return (
		<React.Fragment>
			{before}
			<HighlightedText>{searchValue}</HighlightedText>
			{after}
		</React.Fragment>
	);
};

export const getColumnValue = ({ column, row, currencySymbol, search }) => {
	if (column.colName === 'period') {
		return `${dateFormatter(row.minDate)}–${dateFormatter(row.maxDate)}`;
	} else if (column.colName === 'value') {
		return getHighlightedName(row[column.colName], search);
	}
	return colFormatters[column.colName]
		? colFormatters[column.colName](row[column.colName], currencySymbol)
		: row[column.colName];
};

export const getCols = (transactions, selectedDimension) => {
	if (!transactions || !transactions.headers || !selectedDimension) {
		return [];
	}
	const groupedColumns = groupBy(transactions.headers, 'colName');
	return [
		{
			align: 'left',
			title: `${transactions.totalCount} items found`,
			colName: 'value',
			sortIndex: groupedColumns.value[0].sortIndex,
			width: 256,
		},
		{
			align: 'right',
			title: 'Spend',
			colName: 'spend',
			sortIndex: groupedColumns.spend[0].sortIndex,
			width: 92,
		},
		{
			align: 'left',
			title: 'Time period',
			colName: 'period',
			sortIndex: groupedColumns.minDate[0].sortIndex,
			width: 188,
		},
		{
			align: 'right',
			title: '# of transactions',
			colName: 'transactionsCount',
			sortIndex: groupedColumns.transactionsCount[0].sortIndex,
			width: 132,
		},
	];
};

export const QUICK_FILTERS_MAP = {
	0: 0,
	'Empty / Null': 1,
	'Contains numerical': 2,
	'Numerical only': 3,
	'Special characters': 4,
};

export const getCustomFilters = props => {
	const { storedQueryId, storedQuery } = props;
	let { customFiltersValues, customFilters } = props;

	if (storedQueryId) {
		if (storedQuery) {
			({ customFiltersValues, customFilters } = storedQuery);
		} else {
			customFiltersValues = [];
			customFilters = [];
		}
	}

	return { customFiltersValues, customFilters };
};

export const getWaterfallFilters = props => {
	const { supplierId, waterfallFilter, isHarmonized } = props;
	const vendorFilterName = !isHarmonized
		? VENDOR_TYPES.original
		: VENDOR_TYPES.harmonized;

	if (
		!waterfallFilter ||
		(supplierId && vendorFilterName === waterfallFilter[0])
	) {
		return [];
	}

	return [waterfallFilter[2]];
};

export const getTags = props => {
	const {
		showNonSourceable,
		includeCustomCategories,
		useNetSpend,
		categoryName,
		categoryHierarchy,
		measures,
		showNegativeSpend,
		dimensionName,
		customFilterOptions,
	} = props;
	const { customFilters } = getCustomFilters(props);
	const spendType =
		showNonSourceable === 'true' ? 'Non-sourceable' : 'Sourceable';
	const waterfallFilters = getWaterfallFilters(props);

	const customFiltersOccurances = customFilters.reduce((acc, filter) => {
		if (!acc[filter]) {
			const customFilterObj = customFilterOptions.find(
				item => item.name === filter
			);
			return {
				...acc,
				[filter]: {
					count: 1,
					label: customFilterObj ? customFilterObj.label : filter,
				},
			};
		}

		return {
			...acc,
			[filter]: { ...acc[filter], count: acc[filter].count + 1 },
		};
	}, {});
	const isCategoryItem = categoryName || categoryHierarchy;
	const taxonomyType = isCategoryItem
		? [!includeCustomCategories ? 'AP Taxonomy' : 'Client taxonomy']
		: [];
	const dimensionFilter = isCategoryItem
		? waterfallFilters
		: measures
			? [measures.slice(-1)[0]]
			: !dimensionName
				? waterfallFilters
				: [];

	return [
		spendType,
		useNetSpend || showNegativeSpend ? 'Net spend' : 'Positive spend',
		...taxonomyType,
		...dimensionFilter,
		...Object.keys(customFiltersOccurances).map(
			name =>
				`${customFiltersOccurances[name].label} (${
					customFiltersOccurances[name].count
				})`
		),
	];
};

export const getRequestParams = props => {
	const {
		companies,
		currency,
		currencyType,
		supplierId,
		dateFrom,
		dateTo,
		hierarchy,
		showNonSourceable,
		isHarmonized,
		customFiltersValues,
		customFilters,
		categoryHierarchy,
		divisionName,
		categoryName,
		dimensionName,
		dimensions,
		includeCustomCategories,
		waterfallFilter,
		selectedDimensionKey,
		selectedDimensionName,
		storedQueryId,
		spendType,
		extraFilters,
		isPmi,
		selectedDimension,
		itemsOnPage,
		search,
		quickFilter,
		sorting,
	} = props;

	if (!selectedDimension) {
		return null;
	}
	const dimensionParam = Array(selectedDimension.level).fill(
		selectedDimension.dimensionName
	);
	const params = {
		harmonized: true,
		includeCustomCategories: !isPmi ? includeCustomCategories : false,
		dimensions: dimensionParam,
		currency,
		currencyType: 'fixed',
		sortDirection: sorting.direction,
		dateFrom,
		dateTo,
		skip: 0,
		take: itemsOnPage,
		sortIndex: sorting.columnIndex,
		SearchTerm: search,
		quickSearchType: QUICK_FILTERS_MAP[quickFilter],
		ContextFilters: [],
		getTailSpend: false,
	};
	const dimensionValue =
		selectedDimensionKey === 'spendType'
			? SPEND_TYPE[selectedDimensionName.toUpperCase()]
			: selectedDimensionName;

	params.dateFrom = dateFrom;
	params.dateTo = dateTo;
	params.filters = ['sourceable'];
	params.filterValues = [showNonSourceable === 'false'];
	companies.forEach(companyName => {
		params.filters.push(COMPANY_FILTER_KEY);
		params.filterValues.push(companyName);
	});
	if (dimensionName) {
		params.filters = [...params.filters, dimensions[0]];
		params.filterValues = [...params.filterValues, dimensionName];
	}

	if (selectedDimensionKey) {
		const filterName = selectedDimensionKey;

		params.filters = [...params.filters, selectedDimensionKey];
		params.filterValues = [...params.filterValues, dimensionValue];

		if (hierarchy) {
			params.hierarchy = hierarchy;
		}

		if (filterName !== dimensions[0]) {
			params.filters = [
				...params.filters,
				...(waterfallFilter ? [waterfallFilter[0]] : []),
			];
			params.filterValues = [
				...params.filterValues,
				...(waterfallFilter ? [waterfallFilter[1]] : []),
			];
		}
	} else if (supplierId) {
		const filterName = !isHarmonized
			? VENDOR_TYPES.original
			: VENDOR_TYPES.harmonized;

		params.filters = [...params.filters, filterName];
		params.filterValues = [...params.filterValues, supplierId];

		if (hierarchy) {
			params.hierarchy = hierarchy;
		}

		if (filterName !== dimensions[0]) {
			params.filters = [
				...params.filters,
				...(waterfallFilter ? [waterfallFilter[0]] : []),
			];
			params.filterValues = [
				...params.filterValues,
				...(waterfallFilter ? [waterfallFilter[1]] : []),
			];
		}
	} else if (categoryHierarchy) {
		params.hierarchy = categoryHierarchy;
		params.filters = [
			...params.filters,
			...(waterfallFilter ? [waterfallFilter[0]] : []),
		];
		params.filterValues = [
			...params.filterValues,
			...(waterfallFilter ? [waterfallFilter[1]] : []),
		];
	} else if (divisionName) {
		params.filters = [
			...params.filters,
			'purchasingLegalEntity',
			...(waterfallFilter ? [waterfallFilter[0]] : []),
		];
		params.filterValues = [
			...params.filterValues,
			divisionName,
			...(waterfallFilter ? [waterfallFilter[1]] : []),
		];

		if (hierarchy) {
			params.hierarchy = hierarchy;
		}
	} else if (categoryName) {
		const filterName = includeCustomCategories
			? 'clientCategoryL4'
			: 'categoryL4';

		params.filters = [
			...params.filters,
			filterName,
			...(waterfallFilter ? [waterfallFilter[0]] : []),
		];
		params.filterValues = [
			...params.filterValues,
			categoryName,
			...(waterfallFilter ? [waterfallFilter[1]] : []),
		];

		if (hierarchy) {
			params.hierarchy = hierarchy;
		}
	}

	if (customFiltersValues.length > 0) {
		params.filters = [...params.filters, ...customFilters];
		params.filterValues = [...params.filterValues, ...customFiltersValues];
	}

	if (isPmi && spendType) {
		params.filters = [...(params.filters || []), 'spendType'];
		params.filterValues = [
			...(params.filterValues || []),
			SPEND_TYPE[spendType],
		];
	}

	if (extraFilters) {
		const filterObj = Object.keys(extraFilters).reduce(
			(acc, filterName) => {
				return {
					...acc,
					filters: [
						...acc.filters,
						...new Array(extraFilters[filterName].length).fill(filterName),
					],
					filterValues: [...acc.filterValues, ...extraFilters[filterName]],
				};
			},
			{
				filters: [],
				filterValues: [],
			}
		);

		params.filters = [...params.filters, ...filterObj.filters];
		params.filterValues = [...params.filterValues, ...filterObj.filterValues];
	}

	if (storedQueryId) {
		params.storedQueryId = storedQueryId;
	}

	if (!isPmi && spendType) {
		params.filters = [...(params.filters || []), 'spendType'];
		params.filterValues = [
			...(params.filterValues || []),
			SPEND_TYPE[spendType],
		];
	}
	if (selectedDimension.filter) {
		params.filters = [...(params.filters || []), selectedDimension.filter];
		params.filterValues = [
			...(params.filterValues || []),
			selectedDimension.filterValue,
		];
	}
	params.currency = currency;
	params.currencyType = currencyType;

	return params;
};

export const getQueryStringState = props => {
	const {
		companiesList,
		unit,
		currency,
		dateFrom,
		dateTo,
		showNonSourceable,
		showAllCategories,
		showTailSpend,
		showAllSegments,
		waterfallShowAdjusted,
		waterfallShowL4Level,
		dimensions,
		tailSpend,
		customFilters,
		customFiltersValues,
		waterfallFilter,
		storedQueryId,
		companies,
		pmiBreadcrumbs,
	} = props;

	const cleanupState = {
		isTailSpend: null,
		hierarchy: null,
		spendType: null,
		harmonizedSupplierId: null,
		harmonizedSupplier: null,
		vendorName: null,
	};

	const queryStringState = {
		...cleanupState,
		unit,
		currency,
		dateFrom,
		dateTo,
		showNonSourceable,
		showAllCategories,
		showTailSpend,
		showAllSegments,
		waterfallShowAdjusted,
		waterfallShowL4Level,
		dimensions,
		tailSpend,
		customFilters,
		customFiltersValues,
		waterfallFilter,
		storedQueryId,
		companies: getCompaniesWithDefaults(companies, companiesList),
		pmiBreadcrumbs,
	};

	return queryStringState;
};

export const showPMIExplorePage = (props, transactionsTitle) => {
	const queryStringState = getQueryStringState(props);

	if (props.selectedDimensionKey) {
		queryStringState.selectedDimensionKey = props.selectedDimensionKey;
		queryStringState.selectedDimensionName = transactionsTitle;
	}

	const search = qs.stringify(queryStringState, {
		strictNullHandling: true,
		arrayFormat: 'repeat',
	});

	const { history } = props;
	const locationUrl = `/pmi/explore?${search}`;

	history.push(locationUrl, {
		prevPageUrl: history.location.pathname + history.location.search,
		transactionsTitle,
	});
};

export const showAnalysisExplorePage = (props, transactionsTitle) => {
	const {
		exploreSupplierId,
		harmonizedSupplierId,
		hierarchy,
		company,
		companies,
		purchasingLegalEntity,
		costCentre,
		date,
		monthYearDate,
		customColumn,
		clientCategoryL4,
		categoryL4,
		segmentName,
		selectedDimensionKey,
		spendType = 'INDIRECT',
		isTailSpend,
		history,
	} = props;
	const { location } = history;

	const currentQueryParams = getQueryParams(location);
	const customColumnObj = customColumn
		? {
				customColumnKey: customColumn.key,
				customColumnValue: customColumn.value,
		  }
		: {};

	const queryStringState = {
		...currentQueryParams,
		supplierId: exploreSupplierId || null,
		harmonizedSupplierId: harmonizedSupplierId || null,
		clientCategoryL4: clientCategoryL4 || null,
		categoryL4: categoryL4 || null,
		isTailSpend: !!isTailSpend,
		segmentName: segmentName || null,
		hierarchy: hierarchy || null,
		company: company || null,
		companies: companies || null,
		costCentre: costCentre || null,
		purchasingLegalEntity: purchasingLegalEntity || null,
		date: date || null,
		monthYearDate: monthYearDate || null,
		spendType: spendType || 'INDIRECT',
		...customColumnObj,
	};

	if (selectedDimensionKey) {
		queryStringState.selectedDimensionKey = selectedDimensionKey;
		queryStringState.selectedDimensionName = transactionsTitle;
	}

	const search = qs.stringify(queryStringState, {
		strictNullHandling: true,
		arrayFormat: 'repeat',
	});

	const locationUrl = `/spend/explore?${search}`;
	history.push(locationUrl, {
		prevPageUrl: location.pathname + location.search,
	});
};
